import { ref } from 'vue'

import type { WorkspaceSearchItem } from '@/services/apiService'
import { generateId } from '@/services/idService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { showError, showInfo, showSuccess } from '@/services/toastService'
import { useEventStore } from '@/stores/eventStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { EventType, type Event } from '@/types/event'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, TourListItem, Waypoint } from '@/types/tour'

export const ADD_TO_COLLECTION_REQUEST_EVENT = 'routecamp:add-to-collection'
export const isAddToCollectionPickerOpen = ref(false)

export interface AddToCollectionTour {
  clientId: string
  name?: string | null
  workspaces?: Array<{ clientId: string; name?: string | null }>
}

export interface AddToCollectionPoint {
  clientId?: string
  workspaceClientId?: string
  lat: number
  lon: number
  ele?: number
  name?: string | null
  description?: string
  time?: number | null
  type?: string | null
}

export interface AddToCollectionPhoto {
  clientId: string
  name?: string | null
}

export interface AddToCollectionPayload {
  tours?: AddToCollectionTour[]
  points?: AddToCollectionPoint[]
  photos?: AddToCollectionPhoto[]
  sourceWorkspaceId?: string | null
  onComplete?: (result: AddToCollectionResult) => void
}

export interface AddToCollectionTarget {
  clientId: string
  name: string
  isPublic: boolean
  isOwner?: boolean
  canEdit?: boolean | null
  canView?: boolean | null
  isBookmarked?: boolean
  coverPhotoThumbnailUrl?: string | null
  ownerNames?: string[] | null
  userRole?: string | null
}

export interface AddToCollectionResult {
  targetWorkspaceId: string
  targetWorkspaceName: string
  toursAdded: number
  pointsAdded: number
  photosAdded: number
  skippedAlreadyPresent: number
  skippedUnsupported: number
}

export const openAddToCollection = (payload: AddToCollectionPayload) => {
  window.dispatchEvent(
    new CustomEvent<AddToCollectionPayload>(ADD_TO_COLLECTION_REQUEST_EVENT, {
      detail: payload,
    }),
  )
}

export const tourToAddToCollectionItem = (tour: Tour | TourListItem): AddToCollectionTour => ({
  clientId: tour.clientId,
  name: tour.name,
  workspaces: 'workspaces' in tour ? tour.workspaces : undefined,
})

export const poiToAddToCollectionPoint = (poi: WorkspacePOI): AddToCollectionPoint => ({
  clientId: poi.clientId,
  workspaceClientId: poi.workspaceClientId,
  lat: poi.lat,
  lon: poi.lon,
  ele: poi.ele,
  name: poi.name,
  description: poi.description,
  time: poi.time,
  type: poi.type,
})

export const waypointToAddToCollectionPoint = (waypoint: Waypoint): AddToCollectionPoint => ({
  lat: waypoint.lat,
  lon: waypoint.lon,
  ele: waypoint.ele,
  name: waypoint.name,
  description: waypoint.description,
  time: waypoint.time,
  type: waypoint.type,
})

const normalizeName = (name: string | null | undefined) => name?.trim() || 'New Collection'

const ensureCatalogWorkspace = (workspace: AddToCollectionTarget) => {
  const searchWorkspaceStore = useSearchWorkspaceStore()
  const existing = searchWorkspaceStore.allWorkspaces.find((w) => w.clientId === workspace.clientId)
  if (existing) return existing

  const created: WorkspaceSearchItem = {
    clientId: workspace.clientId,
    name: workspace.name,
    isPublic: workspace.isPublic,
    isOwner: workspace.isOwner ?? true,
    canEdit: workspace.canEdit ?? true,
    canView: workspace.canView ?? true,
    isBookmarked: workspace.isBookmarked,
    ownerNames: workspace.ownerNames,
    userRole: workspace.userRole,
    coverPhotoThumbnailUrl: workspace.coverPhotoThumbnailUrl ?? null,
    type: 0,
    tours: [],
  }
  searchWorkspaceStore.allWorkspaces.unshift(created)
  return created
}

export const createCollectionForAddTarget = async (
  name: string,
): Promise<AddToCollectionTarget> => {
  const workspaceClientId = generateId()
  const workspaceName = normalizeName(name)
  const eventStore = useEventStore()

  await eventStore.addEvent({
    type: EventType.WORKSPACE_CREATED,
    payload: {
      clientId: workspaceClientId,
      name: workspaceName,
      isPublic: false,
      markedForDeletion: false,
      deletionScheduledAt: null,
      canEdit: true,
    },
  })

  const workspace = {
    clientId: workspaceClientId,
    name: workspaceName,
    isPublic: false,
    isOwner: true,
    canEdit: true,
    canView: true,
  }
  ensureCatalogWorkspace(workspace)
  return workspace
}

export const addPayloadToCollection = async (
  payload: AddToCollectionPayload,
  target: AddToCollectionTarget,
): Promise<AddToCollectionResult> => {
  const eventStore = useEventStore()
  const result: AddToCollectionResult = {
    targetWorkspaceId: target.clientId,
    targetWorkspaceName: target.name,
    toursAdded: 0,
    pointsAdded: 0,
    photosAdded: 0,
    skippedAlreadyPresent: 0,
    skippedUnsupported: 0,
  }
  const events: Array<Omit<Event, 'clientId' | 'timestamp'>> = []
  const catalogWorkspace = ensureCatalogWorkspace(target)
  const pendingTourMemberships: AddToCollectionTour[] = []
  const pendingTourIds = new Set<string>()

  for (const tour of payload.tours ?? []) {
    const alreadyPresent =
      tour.workspaces?.some((workspace) => workspace.clientId === target.clientId) ||
      catalogWorkspace.tours.some((existingTour) => existingTour.clientId === tour.clientId) ||
      pendingTourIds.has(tour.clientId)

    if (alreadyPresent) {
      result.skippedAlreadyPresent += 1
      continue
    }

    events.push({
      type: EventType.TOUR_ADDED_TO_WORKSPACE,
      payload: {
        client_id: tour.clientId,
        workspace_client_id: target.clientId,
      },
    })
    pendingTourIds.add(tour.clientId)
    pendingTourMemberships.push(tour)
    result.toursAdded += 1
  }

  for (const point of payload.points ?? []) {
    events.push({
      type: EventType.WORKSPACE_POI_CREATED,
      payload: {
        clientId: generateId(),
        workspaceClientId: target.clientId,
        sourceWorkspaceClientId: target.clientId,
        isPinned: false,
        canEdit: true,
        lat: point.lat,
        lon: point.lon,
        ele: point.ele,
        name: point.name ?? undefined,
        description: point.description ?? undefined,
        time: point.time ?? undefined,
        type: point.type ?? undefined,
      },
    })
    result.pointsAdded += 1
  }

  if ((payload.photos ?? []).length > 0) {
    result.skippedUnsupported += payload.photos?.length ?? 0
  }

  if (events.length > 0) {
    await eventStore.addEvents(events)
  }

  for (const tour of pendingTourMemberships) {
    if (!tour.workspaces?.some((workspace) => workspace.clientId === target.clientId)) {
      tour.workspaces?.push({ clientId: target.clientId, name: target.name })
    }
    if (!catalogWorkspace.tours.some((existingTour) => existingTour.clientId === tour.clientId)) {
      catalogWorkspace.tours.push({
        clientId: tour.clientId,
        name: normalizeName(tour.name),
        tracks: [],
      })
    }
  }

  const added = result.toursAdded + result.pointsAdded + result.photosAdded
  if (added > 0) {
    showSuccess(`Added ${added} item${added === 1 ? '' : 's'} to ${target.name}.`)
  }
  if (result.skippedAlreadyPresent > 0) {
    showInfo(
      `${result.skippedAlreadyPresent} item${
        result.skippedAlreadyPresent === 1 ? ' was' : 's were'
      } already in ${target.name}.`,
    )
  }
  if (result.skippedUnsupported > 0) {
    showInfo(
      `${result.skippedUnsupported} selected item${
        result.skippedUnsupported === 1 ? '' : 's'
      } could not be added to a collection yet.`,
    )
  }

  logService.info(LOG_CATEGORIES.UI, 'Added payload to collection', {
    targetWorkspaceId: target.clientId,
    toursAdded: result.toursAdded,
    pointsAdded: result.pointsAdded,
    photosAdded: result.photosAdded,
    skippedAlreadyPresent: result.skippedAlreadyPresent,
    skippedUnsupported: result.skippedUnsupported,
  })

  payload.onComplete?.(result)
  return result
}

export const addPayloadToCollectionWithToasts = async (
  payload: AddToCollectionPayload,
  target: AddToCollectionTarget,
) => {
  try {
    return await addPayloadToCollection(payload, target)
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to add payload to collection', { error })
    showError(
      `Failed to add to collection: ${error instanceof Error ? error.message : String(error)}`,
    )
    throw error
  }
}
